/**
 * "A new version of AIM is ready — Reload" after a portal deploy.
 *
 * Owner, 2026-09-28: "if we develop new thing, server or application, it
 * should tell me first". A tab left open keeps running the old build until it
 * is reloaded, so after each deploy this compares the build the page is
 * running (its `/assets/index-*.js` entry) with the one the server now serves,
 * every 5 minutes and whenever the tab comes back into view.
 */
import { useEffect, useState } from "react";
import { RefreshCw, X } from "lucide-react";

const CHECK_EVERY_MS = 5 * 60 * 1000;
const ENTRY_RE = /\/assets\/index-[A-Za-z0-9_-]+\.js/;

/** The entry bundle named in an index.html, or null. */
export function entryOf(html: string): string | null {
  return html.match(ENTRY_RE)?.[0] ?? null;
}

/** The entry bundle THIS page loaded. */
function runningEntry(): string | null {
  for (const s of Array.from(document.querySelectorAll<HTMLScriptElement>("script[src]"))) {
    const m = s.src.match(ENTRY_RE);
    if (m) return m[0];
  }
  return null;
}

export function NewVersionBanner() {
  const [stale, setStale] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const running = runningEntry();
    if (!running) return; // dev server: no hashed bundle to compare
    let cancelled = false;
    const check = async () => {
      try {
        const res = await fetch("/index.html", { cache: "no-store" });
        if (!res.ok) return;
        const served = entryOf(await res.text());
        if (!cancelled && served && served !== running) setStale(true);
      } catch {
        // offline — try again next time
      }
    };
    void check();
    const timer = window.setInterval(check, CHECK_EVERY_MS);
    const onVisible = () => document.visibilityState === "visible" && void check();
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  if (!stale || dismissed) return null;
  return (
    <div
      role="status"
      data-testid="new-version-banner"
      className="fixed bottom-4 left-1/2 z-[90] flex -translate-x-1/2 items-center gap-3 rounded-[var(--cp-r-control)] border border-[var(--cp-accent)] bg-[var(--cp-surface)] px-4 py-2.5 text-sm text-[var(--cp-ink)] shadow-[var(--cp-shadow-lg)]"
    >
      <span>A new version of AIM is ready.</span>
      <button
        type="button"
        onClick={() => window.location.reload()}
        className="inline-flex items-center gap-1.5 rounded-[var(--cp-r-control)] bg-[var(--cp-accent)] px-3 py-1.5 text-xs font-semibold text-white"
      >
        <RefreshCw className="h-3.5 w-3.5" aria-hidden /> Reload
      </button>
      <button
        type="button"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
        className="text-[var(--cp-ink-3)] hover:text-[var(--cp-ink)]"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
